import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";

/**
 * Password input with a show/hide eye toggle.
 *
 * Props:
 *   value, onChange       — controlled
 *   placeholder, autoComplete, required, name, id, autoFocus
 *   testId                — base; the input gets `${testId}`, the toggle gets `${testId}-toggle`
 *   className             — applied to the <input>
 *   minLength             — passed straight to the input
 *
 * Notes:
 *   - Toggle is a real <button type="button"> so it doesn't submit forms.
 *   - Uses aria-pressed + visually-hidden label for screen readers.
 */
export default function PasswordInput({
    value,
    onChange,
    placeholder = "••••••••",
    autoComplete = "current-password",
    required = false,
    name = "password",
    id,
    autoFocus = false,
    testId = "password",
    className = "",
    minLength,
    onKeyDown,
}) {
    const [visible, setVisible] = useState(false);
    return (
        <div className="relative">
            <input
                id={id}
                type={visible ? "text" : "password"}
                name={name}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={placeholder}
                autoComplete={autoComplete}
                required={required}
                autoFocus={autoFocus}
                minLength={minLength}
                data-testid={testId}
                className={`pr-11 ${className}`}
            />
            <button
                type="button"
                onClick={() => setVisible((v) => !v)}
                aria-label={visible ? "Hide password" : "Show password"}
                aria-pressed={visible}
                data-testid={`${testId}-toggle`}
                tabIndex={-1}
                className="absolute right-3 top-1/2 -translate-y-1/2 h-7 w-7 rounded-md flex items-center justify-center text-stone-400 hover:text-stone-700 hover:bg-stone-100 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500/40"
            >
                {visible ? <EyeOff className="w-4 h-4" strokeWidth={1.75} /> : <Eye className="w-4 h-4" strokeWidth={1.75} />}
            </button>
        </div>
    );
}
